/**
 * Risk Trend Chart - Historical risk evolution
 * Shows historical trend for volatility or VaR when data is provided
 * PRIMARY VALUE: RM storytelling about risk trends
 */

import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, BarChart3 } from 'lucide-react';

export interface TrendDataPoint {
  date: string;
  volatility: number;
  var: number;
}

interface RiskTrendChartProps {
  currentVolatility: number;
  currentVaR: number;
  currency?: string;
  historicalData?: TrendDataPoint[];
}

type MetricType = 'volatility' | 'var';

const RiskTrendChart: React.FC<RiskTrendChartProps> = ({
  currentVolatility,
  currentVaR,
  currency = 'CHF',
  historicalData
}) => {
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('volatility');

  const hasHistoricalData = historicalData && historicalData.length >= 2;

  // Calculate trend (increasing/decreasing)
  const trend = useMemo(() => {
    if (!hasHistoricalData) return { direction: 'neutral' as const, percentage: 0 };

    const first = selectedMetric === 'volatility'
      ? historicalData[0].volatility
      : historicalData[0].var;
    const last = selectedMetric === 'volatility'
      ? historicalData[historicalData.length - 1].volatility
      : historicalData[historicalData.length - 1].var;

    if (first === 0) return { direction: 'neutral' as const, percentage: 0 };

    const change = ((last - first) / first) * 100;

    return {
      direction: change > 5 ? 'up' as const : change < -5 ? 'down' as const : 'neutral' as const,
      percentage: Math.abs(change)
    };
  }, [historicalData, hasHistoricalData, selectedMetric]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getTrendColor = () => {
    if (trend.direction === 'up') return 'text-red-600';
    if (trend.direction === 'down') return 'text-green-600';
    return 'text-gray-600';
  };

  const getTrendBgColor = () => {
    if (trend.direction === 'up') return 'bg-red-50 border-red-200';
    if (trend.direction === 'down') return 'bg-green-50 border-green-200';
    return 'bg-gray-50 border-gray-200';
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Risk Evolution</h3>

        {/* Metric Toggle */}
        <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1">
          <button
            onClick={() => setSelectedMetric('volatility')}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              selectedMetric === 'volatility'
                ? 'bg-kielis-navy text-white shadow-sm'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            Volatility
          </button>
          <button
            onClick={() => setSelectedMetric('var')}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              selectedMetric === 'var'
                ? 'bg-kielis-navy text-white shadow-sm'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            VaR
          </button>
        </div>
      </div>

      {hasHistoricalData ? (
        <>
          {/* Trend Indicator */}
          <div className={`mb-4 p-3 rounded-lg border ${getTrendBgColor()}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {trend.direction === 'up' ? (
                  <TrendingUp className={`w-5 h-5 ${getTrendColor()}`} />
                ) : trend.direction === 'down' ? (
                  <TrendingDown className={`w-5 h-5 ${getTrendColor()}`} />
                ) : (
                  <div className="w-5 h-1 bg-gray-400 rounded" />
                )}
                <span className={`font-semibold ${getTrendColor()}`}>
                  {trend.direction === 'up' ? 'Increasing' : trend.direction === 'down' ? 'Decreasing' : 'Stable'}
                </span>
              </div>
              <div className="text-sm">
                <span className="text-gray-600">Period change: </span>
                <span className={`font-semibold ${getTrendColor()}`}>
                  {trend.direction === 'up' ? '+' : trend.direction === 'down' ? '-' : ''}
                  {trend.percentage.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          {/* Chart */}
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={historicalData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                stroke="#6B7280"
              />
              <YAxis
                tick={{ fontSize: 12 }}
                stroke="#6B7280"
                tickFormatter={(value) =>
                  selectedMetric === 'volatility'
                    ? `${value}%`
                    : formatCurrency(value)
                }
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                formatter={(value: number) => [
                  selectedMetric === 'volatility'
                    ? `${value.toFixed(1)}%`
                    : formatCurrency(value),
                  selectedMetric === 'volatility' ? 'Volatility' : 'Value at Risk'
                ]}
              />
              <Line
                type="monotone"
                dataKey={selectedMetric}
                stroke={selectedMetric === 'volatility' ? '#F7B500' : '#DC3545'}
                strokeWidth={3}
                dot={{ fill: selectedMetric === 'volatility' ? '#F7B500' : '#DC3545', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </>
      ) : (
        /* Empty state when no historical data */
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <BarChart3 className="w-12 h-12 text-gray-300 mb-3" />
          <p className="text-gray-600 font-medium mb-1">No historical risk data available</p>
          <p className="text-sm text-gray-500">
            Historical trend data will appear here when available
          </p>
        </div>
      )}

      {/* Current Value Display */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Current {selectedMetric === 'volatility' ? 'Volatility' : 'VaR (95%)'}:</span>
          <span className="text-lg font-bold text-gray-900">
            {selectedMetric === 'volatility'
              ? `${currentVolatility.toFixed(1)}%`
              : formatCurrency(currentVaR)
            }
          </span>
        </div>
      </div>
    </div>
  );
};

export default RiskTrendChart;
